import React, { useState } from 'react';
import { ChevronDown, Heart, Phone, ShoppingCart } from 'lucide-react';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';

const REFINEMENT_QUESTIONS = [
  'What can I celebrate about yesterday?',
  'What did I learn from that?',
  'What is my pressing goal?',
  'What is the fear that comes up when I think of that goal?',
  'What do my core values say to do? Create a list.',
  'From that list, what action can I take today?',
];

interface ValuesSectionProps {
  value1: string;
  value2: string;
  value3: string;
  hasPurchased: boolean;
}

export default function ValuesSection({ value1, value2, value3, hasPurchased }: ValuesSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();

  const values = [value1, value2, value3].filter(Boolean);

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <Heart className="w-5 h-5 text-[#236caf]" />
          Your Values
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="px-4 pb-8 sm:px-6 space-y-6 text-gray-700">

          {/* Core values pills */}
          <div>
            <p className="text-sm font-semibold text-[#004060] uppercase tracking-wide mb-3">Your Core Values</p>
            <div className="flex flex-wrap gap-2">
              {values.map((v, i) => (
                <span
                  key={i}
                  className="px-4 py-2 bg-[#004060] text-white rounded-full text-sm font-semibold"
                >
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* Refinement Engine */}
          <div>
            <p className="text-sm font-semibold text-[#004060] uppercase tracking-wide mb-1">The Refinement Engine</p>
            <p className="text-sm text-gray-500 mb-4 leading-relaxed">
              Run these six questions against each of your values and your current challenge. Do this every day and watch what shifts.
            </p>
            <ol className="space-y-3">
              {REFINEMENT_QUESTIONS.map((q, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#236caf] text-white text-xs flex items-center justify-center font-bold mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-gray-700 italic">{q}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* CTAs */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <a
              href="https://certaingrowth.com/alignmentsession/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#004060] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#236caf] transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              Schedule an Alignment Call
            </a>

            {!hasPurchased && (
              <button
                onClick={() => navigate('/pricing')}
                className="flex items-center justify-center gap-2 w-full bg-[#318231] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#236caf] transition-colors duration-200"
              >
                <ShoppingCart className="w-4 h-4" />
                Join the 21 Day Challenge
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
